import { AuthorizationWrapper } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { AsyncComponent } from '@frontend/components/common';
import { DetailItem, ObjectDetail } from '@frontend/components/detail';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { OrderClient, OrderExtendedClient } from '@frontend/order/api';
import { OrderStateSelect } from '@frontend/order/select';
import { ExtendedOrder, OrderStatusName } from '@frontend/order/types';
import { WorkflowType } from '@frontend/workflow/types';
import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';

import { AdminObjectBadge } from '../../components/admin-object-badge/admin-object-badge.component';
import { OrderStatusBadge } from '../../modal/order-status-badge/order-status-badge';
import { useOrderDetail } from './order-detail.controller';

const ID = 'order-detail';
const WorkflowSelectV2 = lazy(() => import('@frontend/workflow/select').then((module) => ({ default: module.WorkflowSelectV2 })));

export const OrderDetail = () => {
    const viewProps = useOrderDetail();

    return (
        <div id={ID}>
            <ObjectDetail<ExtendedOrder>
                key={viewProps.detailVersion}
                entityType={EntityType.ORDER}
                reactive
                fetch={(accountId: string, orderId: string) => OrderExtendedClient.fetchOrderExtended(accountId, orderId)}
                urlParamKeys={['accountId', 'orderId']}
                baseRoute='/orders'
                delete={{
                    onDelete: (accountId, orderId) => OrderClient.deleteOrder(accountId, orderId),

                    propertyName: CommonMessage.OBJECTS.ORDER.DEFAULT,
                    displayName: (order) => order.id,
                    requiredPermissions: (obj) => [
                        { item: EntityType.ORDER, source: EntityType.ACCOUNT, right: Rights.DELETE, uid: obj.account_id },
                        { item: EntityType.ORDER, source: EntityType.ORDER, right: Rights.DELETE, uid: obj.id }
                    ]
                }}
                update={{
                    onUpdate: () => viewProps.navigate('../update'),
                    requiredPermissions: (obj) => [
                        { item: EntityType.ORDER, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: obj.account_id },
                        { item: EntityType.ORDER, source: EntityType.ORDER, right: Rights.UPDATE, uid: obj.id }
                    ]
                }}
                id={ID}
                cardClassName='card mt-3 w-full lg:w-[calc(50%-8px)]'
                objectHeader={(obj) => obj.id}
                order={['number', 'state', 'user_id', 'product_id', 'quantity', 'spot_id', 'creation_timestamp', 'update_timestamp']}
                keyOverwrite={
                    new Map([
                        ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                        ['product_id', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                        ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.AMOUNT],
                        ['spot_id', CommonMessage.OBJECTS.SPOT.DEFAULT]
                    ])
                }
                object={viewProps.setObject}
                valueOverwrite={
                    new Map([
                        ['state', (obj: ExtendedOrder) => <OrderStatusBadge status={obj.state} />],
                        [
                            'user_id',
                            (obj: ExtendedOrder) => (
                                <AdminObjectBadge
                                    id={obj.user_id}
                                    entityType={EntityType.USER}
                                    display={obj.user?.name}
                                />
                            )
                        ],
                        [
                            'product_id',
                            (obj: ExtendedOrder) => (
                                <AdminObjectBadge
                                    id={obj.product_id}
                                    entityType={EntityType.PRODUCT}
                                    display={obj.product?.name}
                                />
                            )
                        ],
                        [
                            'spot_id',
                            (obj: ExtendedOrder) =>
                                obj.spot_id ? (
                                    <AdminObjectBadge
                                        id={obj.spot_id}
                                        entityType={EntityType.SPOT}
                                        display={obj.spot?.name}
                                    />
                                ) : (
                                    <span>/</span>
                                )
                        ]
                    ])
                }>
                {(object) => (
                    <DetailItem
                        identifier='Main'
                        main>
                        <div className='flex w-full flex-col lg:w-[calc(50%-8px)]'>
                            <AuthorizationWrapper
                                requiredPermissions={[{ item: EntityType.WORKFLOW, source: EntityType.ACCOUNT, right: Rights.READ, uid: object.account_id }]}>
                                <div className='card mt-3 w-full'>
                                    <div className='card-header flex justify-between'>
                                        <h4>{CommonMessage.OBJECTS.WORKFLOW.DEFAULT}</h4>
                                        {!viewProps.updateWorkflow ? (
                                            <AuthorizationWrapper
                                                requiredPermissions={[
                                                    { item: EntityType.ORDER, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: object.account_id },
                                                    { item: EntityType.ORDER, source: EntityType.ORDER, right: Rights.UPDATE, uid: object.id }
                                                ]}>
                                                <button
                                                    id={object.workflow_id ? 'update-button' : 'link-button'}
                                                    className='btn btn-status-info m-0 inline-flex justify-center'
                                                    onClick={viewProps.onUpdateWorkflow}>
                                                    {object.workflow_id ? <ICONS.BUTTON.UPDATE className='me-2' /> : <ICONS.BUTTON.LINK className='me-2' />}
                                                    {object.workflow_id ? CommonMessage.BUTTONS.UPDATE : CommonMessage.BUTTONS.LINK}
                                                </button>
                                            </AuthorizationWrapper>
                                        ) : (
                                            <span>
                                                <button
                                                    id='cancel-button'
                                                    className='btn btn-brand-secondary m-0 me-2'
                                                    onClick={() => viewProps.setUpdateWorkflow(null)}>
                                                    {CommonMessage.BUTTONS.CANCEL}
                                                </button>
                                                <button
                                                    id='link-button'
                                                    className='btn btn-status-success m-0'
                                                    onClick={viewProps.onUpdateWorkflow}>
                                                    <ICONS.BUTTON.LINK className='me-2' />
                                                    {CommonMessage.BUTTONS.SUBMIT}
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    {!viewProps.updateWorkflow ? (
                                        <div className='card-body'>
                                            {object.workflow_id ? (
                                                <AdminObjectBadge
                                                    entityType={EntityType.WORKFLOW}
                                                    id={object.workflow_id}
                                                />
                                            ) : (
                                                <p>
                                                    <FormattedMessage
                                                        id='order-detail.no-workflow'
                                                        defaultMessage='No workflow is assigned to this order'
                                                        description='No workflow is assigned to this order message on the order detail page'
                                                    />
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='card-body'>
                                            <AsyncComponent>
                                                <WorkflowSelectV2
                                                    queryParams={{ account_id: object.account_id, type: WorkflowType.ORDER }}
                                                    onChange={(id: string | null) =>
                                                        viewProps.setUpdateWorkflow({ ...viewProps.updateWorkflow, workflow_id: id })
                                                    }
                                                    value={viewProps.updateWorkflow.workflow_id}
                                                    isClearable
                                                />
                                            </AsyncComponent>
                                        </div>
                                    )}
                                </div>
                            </AuthorizationWrapper>
                            {object.workflow_id && (
                                <AuthorizationWrapper
                                    requiredPermissions={[
                                        { item: EntityType.ORDER, source: EntityType.ACCOUNT, right: Rights.STATE, uid: object.account_id },
                                        { item: EntityType.ORDER, source: EntityType.ORDER, right: Rights.STATE, uid: object.id }
                                    ]}>
                                    <div className='card mt-3 w-full'>
                                        <div className='card-header flex justify-between'>
                                            <h4>{CommonMessage.OBJECTS.WORKFLOW.CHANGE_STATE}</h4>
                                            {!viewProps.updateState ? (
                                                <button
                                                    className='btn btn-status-warning'
                                                    id='change-state-button'
                                                    onClick={viewProps.onUpdateState}>
                                                    <ICONS.BUTTON.CHANGE_STATE className='me-2' />
                                                    {CommonMessage.BUTTONS.CHANGE_STATE}
                                                </button>
                                            ) : (
                                                <span>
                                                    <button
                                                        id='cancel-button'
                                                        className='btn btn-brand-secondary me-2'
                                                        onClick={() => viewProps.setUpdateState(null)}>
                                                        {CommonMessage.BUTTONS.CANCEL}
                                                    </button>
                                                    <button
                                                        className='btn btn-status-success'
                                                        id='submit-button'
                                                        onClick={viewProps.onUpdateState}>
                                                        <ICONS.BUTTON.CHANGE_STATE className='me-2' />
                                                        {CommonMessage.BUTTONS.SUBMIT}
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                        {!viewProps.updateState ? (
                                            <div className='card-body'>
                                                <OrderStatusBadge status={object.state} />
                                            </div>
                                        ) : (
                                            <div className='card-body'>
                                                <OrderStateSelect
                                                    value={viewProps.updateState.state}
                                                    states={Object.values(OrderStatusName).filter((state) => state !== OrderStatusName.ANY)}
                                                    onSelect={(value) =>
                                                        viewProps.setUpdateState({
                                                            ...viewProps.updateState,
                                                            state: value?.value as OrderStatusName | undefined
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </AuthorizationWrapper>
                            )}
                        </div>
                    </DetailItem>
                )}
            </ObjectDetail>
        </div>
    );
};
